import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const Label = ({ children }) => <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">{children}</p>;

export const InternalNotes = ({ item, onUpdated, fmt }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = Array.isArray(item.notes_log) ? item.notes_log : [];

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/enquiries/${item.id}/notes`, { text });
      onUpdated(data);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8" data-testid="admin-notes-log">
      <Label>Internal notes · staff only</Label>
      <ul className="mt-3 space-y-2">
        {notes.length === 0 && <li className="text-sm text-stone-400">No internal notes yet.</li>}
        {notes.map((n, i) => (
          <li key={`${n.at}-${i}`} className="rounded-xl border border-white/10 bg-black/30 p-3 text-sm">
            <p className="whitespace-pre-wrap text-stone-200">{n.text}</p>
            <p className="mt-1.5 text-[11px] text-stone-400">{n.by} · {fmt(n.at)}</p>
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} placeholder="Add a note (call outcome, next step…)"
          aria-label="New internal note" data-testid="admin-note-input"
          className="h-11 flex-1 rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-stone-100 outline-none focus:border-orange-500" />
        <button disabled={busy || !text.trim()} data-testid="admin-note-add"
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-orange-500/50 px-4 text-sm text-orange-200 hover:bg-orange-500/10 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add note
        </button>
      </form>
    </div>
  );
};

const NOTIF = { sent: "text-emerald-300", failed: "text-red-300", skipped: "text-amber-300" };

export const ActivityLog = ({ item, fmt }) => {
  const timeline = Array.isArray(item.timeline) ? [...item.timeline].reverse() : [];
  const notifs = Array.isArray(item.notifications) ? item.notifications : [];
  return (
    <div className="mt-8 grid gap-6 sm:grid-cols-2">
      <div data-testid="admin-timeline">
        <Label>Activity timeline</Label>
        <ol className="mt-3 space-y-3 border-l border-white/10 pl-4">
          {timeline.length === 0 && <li className="text-sm text-stone-400">Received {fmt(item.created_at)}</li>}
          {timeline.map((t, i) => (
            <li key={`${t.at}-${i}`} className="relative text-sm">
              <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-orange-500" />
              <span className="text-stone-200">{t.action}</span>
              <span className="block text-[11px] text-stone-400">{t.by} · {fmt(t.at)}</span>
            </li>
          ))}
        </ol>
      </div>
      <div data-testid="admin-notifications">
        <Label>Email notifications</Label>
        <ul className="mt-3 space-y-2 text-sm">
          {notifs.length === 0 && <li className="text-stone-400">None recorded.</li>}
          {notifs.map((n, i) => (
            <li key={`${n.at}-${i}`}>
              <span className={`font-mono text-[11px] uppercase ${NOTIF[n.status] ?? "text-stone-300"}`}>{n.status}</span>
              <span className="text-stone-300"> · {n.kind}{n.to ? ` → ${n.to}` : ""}</span>
              {n.detail && <span className="block text-[11px] text-stone-400">{n.detail}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
